/**
 * A campaign created through the Campaign Companion, as `submitted_via` names
 * it on the contest record.
 *
 * Shared because the rule is applied on two screens that do not otherwise talk
 * to each other -- the dashboard's pending list and the campaigns table -- and
 * a second copy of the matching would drift.
 */
const CAMPAIGN_COMPANION = 'campaign companion';

/**
 * Whether the contest was submitted through the Campaign Companion.
 *
 * Case, surrounding space and underscores are tolerated so a record written as
 * "campaign_companion" is not missed, but the match is otherwise exact: a
 * loose "contains companion" test would also catch a future
 * "WhatsApp Companion" and silently relabel it.
 */
export function isSubmittedViaCampaignCompanion(contest: any): boolean {
  const raw = contest?.submitted_via;
  if (raw === null || raw === undefined) return false;

  return String(raw).trim().toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ')
    === CAMPAIGN_COMPANION;
}

/**
 * The status a Campaign Companion submission shows while it waits.
 *
 * A plain "Pending" does not say what it is pending on. These campaigns are
 * queued for someone to approve, so they are labelled as such -- but only the
 * label changes: `status` is still 'pending' on the record, and the status
 * filter still matches it as pending.
 */
export const PENDING_APPROVAL_STATUS = 'pending_approval';

/**
 * Maps a contest's own status to the one shown on screen.
 *
 * Returns 'pending_approval' for a pending Campaign Companion submission and
 * the status unchanged for everything else, so callers can apply it without
 * first checking whether it applies.
 */
export function applyCompanionPendingStatus(contest: any, status: string): string {
  return status === 'pending' && isSubmittedViaCampaignCompanion(contest)
    ? PENDING_APPROVAL_STATUS
    : status;
}
